import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useSpacetimeDB, useTable } from "spacetimedb/react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { Address } from "@blaze-cardano/core";

import { GummiwormContext } from "./context";
import { tables } from "@module-bindings";
import type { IGummiwormState } from "./types";
import { useDepositTxs } from "./hooks/useDepositTxs";
import { useWithdrawTxs } from "./hooks/useWithdrawTxs";
import { useAppContext } from "../AppContext";
import { Core } from "@blaze-cardano/sdk";
import { hashHexToU256 } from "@/lib/marketUtils";

export const MOCK_USER_ACCOUNT_ID = 1n;

export const GummiwormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { blaze },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx, clearDepositTxs } = useDepositTxs();
  const { withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs } = useWithdrawTxs();
  const connection = useSpacetimeDB();
  const [accounts, accountsReady] = useTable(tables.accounts);
  const [markets] = useTable(tables.markets);
  const [appConfig] = useTable(tables.app_config);
  const tradingEnabled = appConfig.length > 0 ? !!appConfig[0].tradingEnabled : false;

  const myAccountId = useMemo(() => {
    if (!mainAddress) return null;
    const hash = Address.fromBech32(mainAddress).getProps().paymentPart?.hash;
    if (!hash) return null;
    return hashHexToU256(hash);
  }, [mainAddress]);
  const [deposits] = useTable(tables.deposits, {
    async onInsert(row) {
      // Ignore deposits belonging to other users
      if (myAccountId != null && row.accountId !== myAccountId) return;

      // Reconcile local tracking with the DB state. On reconnect/wallet
      // switch, already-absorbed deposits arrive via onInsert, not onUpdate.
      if (row.absorbedInBlock !== 0n) {
        if (depositTxs.has(row.requestId.toString())) {
          updateDepositTx(row.requestId, { status: "absorbed" });
        }
        return;
      }
      if (row.rejectedInBlock !== 0n) {
        if (depositTxs.has(row.requestId.toString())) {
          updateDepositTx(row.requestId, {
            status: "rejected",
            errorMessage: row.failureReason ?? "Deposit was rejected by the network.",
          });
        }
        return;
      }
      if (row.refundTx) {
        const refundCbor = Buffer.from(row.refundTx).toString("hex");
        if (depositTxs.has(row.requestId.toString())) {
          updateDepositTx(row.requestId, { refundCbor, status: "confirmed" });
        }
        return;
      }

      if (!depositTxs.has(row.requestId.toString())) {
        addDepositTx(row.requestId, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "pending",
        });
      } else {
        updateDepositTx(row.requestId, { status: "pending" });
      }
    },
    async onUpdate(oldRow, newRow) {
      const requestId = newRow.requestId;
      // Ignore deposits belonging to other users
      if (myAccountId != null && newRow.accountId !== myAccountId) return;

      // Refund tx received — just store it. The reconciler will submit.
      if (!oldRow.refundTx && newRow.refundTx) {
        const refundCbor = Buffer.from(newRow.refundTx).toString("hex");
        if (!depositTxs.has(requestId.toString())) {
          addDepositTx(requestId, { cbor: "", hash: "", refundCbor, status: "confirmed" });
        } else {
          updateDepositTx(requestId, { refundCbor, status: "confirmed" });
        }
        return;
      }

      // Deposit absorbed into L2
      if (newRow.absorbedInBlock && !oldRow.absorbedInBlock) {
        updateDepositTx(requestId, { status: "absorbed" });
        return;
      }

      // Deposit rejected
      if (newRow.rejectedInBlock && !oldRow.rejectedInBlock) {
        updateDepositTx(requestId, {
          status: "rejected",
          errorMessage:
            newRow.failureReason ?? "Deposit was rejected by the network.",
        });
        return;
      }

      // Processing error
      if (newRow.failureReason && !oldRow.failureReason) {
        updateDepositTx(requestId, {
          status: "failed",
          errorMessage: newRow.failureReason,
        });
      }
    },
  });

  // ── Deposit reconciler ──────────────────────────────────────────────
  // Single place that handles L1 submission and confirmation monitoring.
  // Runs whenever depositTxs or blaze changes. Uses a ref to track which
  // deposits are already being handled so we don't double-submit.
  const handling = useRef(new Set<string>());

  useEffect(() => {
    if (!blaze || !blaze.provider) return;

    for (const [id, tx] of depositTxs.entries()) {
      if (handling.current.has(id)) continue;

      // Ready to submit: has both signed cbor and refund tx, but not yet submitted
      if (
        tx.cbor &&
        tx.refundCbor &&
        !tx.hash &&
        !["settling", "on-chain", "absorbed", "failed", "rejected", "refunded", "refunding"].includes(tx.status)
      ) {
        handling.current.add(id);
        (async () => {
          try {
            const signedTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.cbor));
            const hash = await blaze.submitTransaction(signedTx);
            updateDepositTx(BigInt(id), { status: "settling", hash });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes("already submitted") || msg.includes("AlreadyExists")) {
              // Someone else submitted — that's fine
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage: `L1 transaction submission failed: ${msg}`,
              });
            }
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Settling: monitor for L1 confirmation
      if (tx.status === "settling" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "on-chain" });
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage:
                  "Deposit transaction was not confirmed on L1 within the expected window.",
              });
            }
          })
          .catch((err) => {
            console.error("Error monitoring L1 tx confirmation:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }

      // Rejected with refund available: submit refund tx
      if (tx.status === "rejected" && tx.refundCbor && !tx.hash) {
        handling.current.add(id);
        (async () => {
          try {
            updateDepositTx(BigInt(id), { status: "refunding" });
            const refundTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.refundCbor));
            const refundHash = await blaze.submitTransaction(refundTx);
            updateDepositTx(BigInt(id), { hash: refundHash, status: "refunding" });
            const confirmed = await blaze.provider.awaitTransactionConfirmation(
              refundHash,
              120_000,
            );
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          } catch (err) {
            console.error("Error submitting refund tx:", err);
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Refunding: monitor for confirmation
      if (tx.status === "refunding" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          })
          .catch((err) => {
            console.error("Error monitoring refund tx:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }
    }
  }, [depositTxs, blaze, updateDepositTx]);

  const contextValue = useMemo<IGummiwormState>(
    () => ({
      connection,
      accounts,
      accountsReady,
      deposits,
      markets,
      myAccountId,
      error: connection.connectionError?.message ?? null,
      tradingEnabled,
      depositTxs,
      clearDepositTxs,
      withdrawTxs,
      addWithdrawTx,
      updateWithdrawTx,
      clearWithdrawTxs,
    }),
    [connection, accounts, accountsReady, connection.connectionError, deposits, markets, myAccountId, tradingEnabled, depositTxs, clearDepositTxs, withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs],
  );

  return (
    <GummiwormContext.Provider value={contextValue}>
      {children}
    </GummiwormContext.Provider>
  );
};
